import { jwtDecode } from "jwt-decode";
import accountContext from "../../account/accountContext";
import { PermissionLevel } from "../models/PermissionModels";

interface JwtPayloadWithPermissions {
  permissions?: string;
  [key: string]: any;
}

/**
 * Parses the "permissions" JWT claim into a map of module -> PermissionLevel.
 * Format: "Machinery:3,Servicing:2,Inventory:1,..."
 * Returns empty map if claim is missing, malformed, or user is not logged in.
 */
const parsePermissions = (permissionString: string): Record<string, PermissionLevel> => {
  const result: Record<string, PermissionLevel> = {};

  if (!permissionString) {
    return result;
  }

  try {
    const pairs = permissionString.split(",");
    for (const pair of pairs) {
      const trimmed = pair.trim();
      if (!trimmed) continue;

      const colonIndex = trimmed.lastIndexOf(":");
      if (colonIndex <= 0) continue;

      const module = trimmed.substring(0, colonIndex);
      const levelStr = trimmed.substring(colonIndex + 1);
      const level = parseInt(levelStr, 10);

      if (!isNaN(level) && level >= PermissionLevel.None && level <= PermissionLevel.Admin) {
        result[module] = level as PermissionLevel;
      }
    }
  } catch {
    // Malformed claim - return empty map
  }

  return result;
};

class PermissionContext {
  /**
   * Returns the parsed permission map from the current JWT token.
   * Keys are module names (e.g., "Machinery"), values are PermissionLevel enums.
   * Returns empty map if not logged in or permissions claim is missing.
   */
  getPermissions(): Record<string, PermissionLevel> {
    const token = accountContext.accessToken;
    if (!token) {
      return {};
    }

    try {
      const decoded = jwtDecode<JwtPayloadWithPermissions>(token);
      if (!decoded.permissions) {
        return {};
      }
      return parsePermissions(decoded.permissions);
    } catch {
      return {};
    }
  }

  /**
   * Returns the permission level for a specific module.
   * Returns PermissionLevel.None if the module is not found or user is not logged in.
   */
  getModulePermission(module: string): PermissionLevel {
    const permissions = this.getPermissions();
    return permissions[module] ?? PermissionLevel.None;
  }

  /**
   * Checks if the user has at least the specified permission level for a module.
   */
  hasPermission(module: string, requiredLevel: PermissionLevel): boolean {
    return this.getModulePermission(module) >= requiredLevel;
  }

  /**
   * Returns true if the JWT contains a permissions claim.
   * Used during transition period: if no claim exists, permission checks are skipped
   * to maintain backward compatibility with old tokens.
   */
  hasPermissionsClaim(): boolean {
    const token = accountContext.accessToken;
    if (!token) {
      return false;
    }

    try {
      const decoded = jwtDecode<JwtPayloadWithPermissions>(token);
      return typeof decoded.permissions === "string" && decoded.permissions.length > 0;
    } catch {
      return false;
    }
  }
}

export default new PermissionContext();
